import { useState } from "react";
import { Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useHub } from "../lib/hubContent";
import { api } from "../lib/peApi";
import { usePEAuth } from "./pe/PEAuthContext";

// --- Schemas ---------------------------------------------------------------
// text | textarea | list_text (one per line) | list_obj (rows of subfields) | color

export const SECTION_SCHEMAS = {
  hero: {
    label: "Hero",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "tagline", label: "Tagline (black pill)" },
      { k: "hand_note", label: "Handwritten note" }, { k: "subtext", label: "Intro paragraph", t: "textarea" },
      { k: "sticker_1", label: "Sticker note (left)" }, { k: "sticker_2", label: "Sticker note (right)" },
      { k: "image", label: "Portrait image URL" }, { k: "image_alt", label: "Portrait alt text" },
      { k: "cta_primary", label: "Primary button label" }, { k: "cta_secondary", label: "Secondary link label" },
    ],
  },
  marquee: { label: "Marquee lines", fields: [{ k: "items", label: "Lines (one per line)", t: "list_text" }] },
  about: {
    label: "About Nelle",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "title_a", label: "Title, first part" }, { k: "title_b", label: "Title, accent part" },
      { k: "para1", label: "First paragraph", t: "textarea" }, { k: "para2", label: "Second paragraph", t: "textarea" },
      { k: "dimensions", label: "Dimension chips (one per line)", t: "list_text" },
      { k: "quote", label: "Pull quote", t: "textarea" }, { k: "cite", label: "Quote signature" },
      { k: "portrait", label: "Portrait image URL" }, { k: "portrait_alt", label: "Portrait alt text" },
      { k: "portrait_caption", label: "Polaroid caption" }, { k: "hand_note", label: "Handwritten side note", t: "textarea" },
    ],
  },
  beauty: {
    label: "Beauty + Style",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "note", label: "Handwritten note" },
      { k: "captions", label: "Photo captions (3, one per line)", t: "list_text" },
      { k: "palette_label", label: "Palette label" },
      { k: "lookbook_url", label: "Lookbook link URL (opens when clicked)" },
      { k: "swatches", label: "Palette swatches", t: "list_obj", sub: [{ k: "name", label: "Name" }, { k: "hex", label: "Color", t: "color" }] },
      { k: "lookbook_toast", label: "Lookbook button message" }, { k: "lookbook_toast_detail", label: "Lookbook message detail", t: "textarea" },
    ],
  },
  music: {
    label: "Music section copy",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "note", label: "Handwritten note" },
      { k: "player_caption", label: "Player caption" }, { k: "tracklist_note", label: "Tracklist footnote", t: "textarea" },
    ],
  },
  brands: {
    label: "Brand ecosystem",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "note", label: "Handwritten note" },
      { k: "brands", label: "Brands", t: "list_obj", sub: [
        { k: "id", label: "ID (letters only)" }, { k: "name", label: "Name" }, { k: "tag", label: "Tagline" },
        { k: "status", label: "Status chip" }, { k: "est", label: "Established label" },
        { k: "desc", label: "Description", t: "textarea" }, { k: "accent", label: "Accent color", t: "color" },
      ]},
    ],
  },
  ideas_head: {
    label: "Ideas section copy",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "title_a", label: "Title, first part" }, { k: "title_b", label: "Title, accent part" },
      { k: "note", label: "Handwritten note" }, { k: "footer_note", label: "Footer note" },
    ],
  },
  poetry_head: {
    label: "Poetry section copy",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "title_a", label: "Title, first part" }, { k: "title_b", label: "Title, accent part" },
      { k: "note", label: "Handwritten note" }, { k: "footer_note", label: "Footer note" },
    ],
  },
  faith: {
    label: "Faith",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "quote", label: "Verse / quote", t: "textarea" },
      { k: "cite", label: "Citation" }, { k: "body", label: "Closing words", t: "textarea" },
    ],
  },
  footer: {
    label: "Footer",
    fields: [
      { k: "eyebrow", label: "Eyebrow line" }, { k: "dispatch_title", label: "Newsletter title" },
      { k: "dispatch_blurb", label: "Newsletter blurb", t: "textarea" }, { k: "copyright", label: "Copyright line" },
      { k: "hand_note", label: "Handwritten note" },
      { k: "socials", label: "Social links", t: "list_obj", sub: [{ k: "id", label: "ID (letters only)" }, { k: "label", label: "Label" }, { k: "url", label: "Link URL (blank = coming soon)" }] },
    ],
  },
};

export const ITEM_SCHEMAS = {
  ideas: {
    label: "idea",
    fields: [
      { k: "title", label: "Title" }, { k: "dek", label: "Summary", t: "textarea" },
      { k: "category", label: "Category" }, { k: "issue", label: "Issue (e.g. Nº 007)" },
      { k: "read", label: "Read time" }, { k: "color", label: "Category color", t: "color" },
    ],
  },
  poems: {
    label: "poem",
    fields: [
      { k: "title", label: "Title" }, { k: "mood", label: "Mood tag" }, { k: "body", label: "Poem (line breaks kept)", t: "textarea", rows: 8 },
    ],
  },
  songs: {
    label: "song",
    fields: [
      { k: "title", label: "Title" }, { k: "year", label: "Year" }, { k: "mood", label: "Mood" },
      { k: "length", label: "Length (e.g. 3:42)" }, { k: "note", label: "Handwritten note" },
      { k: "gradient", label: "Cover gradient (Tailwind classes)" },
      { k: "lyrics", label: "Lyrics excerpt (one line per row)", t: "list_text" },
    ],
  },
};

const inputCls =
  "w-full rounded-lg border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-stone-900 outline-none focus:border-stone-700 transition-colors";

function Field({ field, value, onChange }) {
  const v = value ?? (field.t?.startsWith("list") ? [] : "");
  if (field.t === "textarea")
    return <textarea rows={field.rows || 3} className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} />;
  if (field.t === "list_text")
    return <textarea rows={5} className={`${inputCls} font-mono text-xs`} value={(v || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />;
  if (field.t === "color")
    return (
      <span className="flex items-center gap-2">
        <input type="color" value={v || "#2563EB"} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 rounded border border-stone-300 cursor-pointer" />
        <input className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} />
      </span>
    );
  if (field.t === "list_obj") {
    const rows = Array.isArray(v) ? v : [];
    const blank = Object.fromEntries(field.sub.map((s) => [s.k, ""]));
    return (
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2 relative">
            <button type="button" aria-label="Remove row" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-stone-400 hover:text-red-600">
              <Trash2 size={14} />
            </button>
            {field.sub.map((s) => (
              <label key={s.k} className="block">
                <span className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">{s.label}</span>
                {s.t === "textarea" ? (
                  <textarea rows={2} className={inputCls} value={row[s.k] || ""} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [s.k]: e.target.value } : r)))} />
                ) : s.t === "color" ? (
                  <input type="color" value={row[s.k] || "#2563EB"} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [s.k]: e.target.value } : r)))} className="w-10 h-9 rounded border border-stone-300 cursor-pointer" />
                ) : (
                  <input className={inputCls} value={row[s.k] || ""} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [s.k]: e.target.value } : r)))} />
                )}
              </label>
            ))}
          </div>
        ))}
        <button type="button" onClick={() => onChange([...rows, blank])} className="inline-flex items-center gap-1.5 text-xs text-stone-600 hover:text-stone-900">
          <Plus size={13} /> Add row
        </button>
      </div>
    );
  }
  return <input className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} />;
}

function EditorModal() {
  const hub = useHub();
  const { editor, setEditor } = hub;
  const isSection = editor.type === "section";
  const schema = isSection ? SECTION_SCHEMAS[editor.key] : ITEM_SCHEMAS[editor.kind];
  const initial = isSection ? hub.section(editor.key) : editor.item || {};
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(initial)));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (isSection) {
        await hub.saveSection(editor.key, draft);
      } else if (editor.item) {
        await hub.updateItem(editor.kind, editor.item.id, draft);
      } else {
        await hub.addItem(editor.kind, draft);
      }
      toast("Saved — live on the site.");
      setEditor(null);
    } catch (e) {
      toast("Couldn't save", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    const data = await hub.resetSection(editor.key);
    setDraft(JSON.parse(JSON.stringify(data)));
    toast("Restored the original wording.");
  };

  const remove = async () => {
    if (!window.confirm(`Delete this ${schema.label}? This cannot be undone.`)) return;
    await hub.removeItem(editor.kind, editor.item.id);
    setEditor(null);
    toast("Deleted.");
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`Edit ${schema.label}`} className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onKeyDown={(e) => e.key === "Escape" && setEditor(null)}>
      <div className="relative bg-[#FBF3E4] w-full max-w-xl rounded-2xl border border-stone-300 shadow-2xl my-8" data-testid="hub-editor-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-300/60">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-stone-500">
            Editing — {schema.label}{!isSection && editor.item ? ` · ${editor.item.title}` : ""}
          </p>
          <button onClick={() => setEditor(null)} aria-label="Close editor" className="p-1.5 text-stone-500 hover:text-stone-900"><X size={17} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[62vh] overflow-y-auto">
          {schema.fields.map((f) => (
            <label key={f.k} className="block">
              <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">{f.label}</span>
              <Field field={f} value={draft[f.k]} onChange={(val) => setDraft((d) => ({ ...d, [f.k]: val }))} />
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-stone-300/60">
          <div className="flex gap-3">
            {isSection && (
              <button onClick={reset} data-testid="hub-editor-reset" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-stone-500 hover:text-stone-900">
                <RotateCcw size={12} /> Original
              </button>
            )}
            {!isSection && editor.item && (
              <button onClick={remove} data-testid="hub-editor-delete" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-red-700 hover:text-red-900">
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
          <button onClick={save} disabled={busy} data-testid="hub-editor-save" className="rounded-full bg-[#241B12] text-[#FBF3E4] px-6 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-en-blue transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditSectionButton({ sectionKey, label }) {
  const hub = useHub();
  if (!hub?.editMode) return null;
  return (
    <button
      data-testid={`hub-edit-section-${sectionKey}`}
      onClick={() => hub.setEditor({ type: "section", key: sectionKey })}
      className="absolute top-4 right-4 z-[80] inline-flex items-center gap-1.5 rounded-full bg-[#241B12] text-[#FBF3E4] px-4 py-2 font-mono text-[9px] tracking-[0.2em] uppercase shadow-lg hover:bg-en-blue transition-colors"
    >
      <Pencil size={11} /> {label || "Edit"}
    </button>
  );
}

export function EditItemButton({ kind, item, className = "" }) {
  const hub = useHub();
  if (!hub?.editMode) return null;
  return (
    <button
      data-testid={`hub-edit-item-${item.id}`}
      onClick={(e) => { e.stopPropagation(); hub.setEditor({ type: "item", kind, item }); }}
      className={`inline-flex items-center gap-1 rounded-full bg-[#241B12]/90 text-[#FBF3E4] px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] uppercase shadow hover:bg-en-blue transition-colors ${className}`}
    >
      <Pencil size={10} /> Edit
    </button>
  );
}

export function AddItemButton({ kind, label, className = "" }) {
  const hub = useHub();
  if (!hub?.editMode) return null;
  return (
    <button
      data-testid={`hub-add-${kind}`}
      onClick={() => hub.setEditor({ type: "item", kind, item: null })}
      className={`inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-current px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase opacity-70 hover:opacity-100 transition-opacity ${className}`}
    >
      <Plus size={12} /> {label || `Add ${kind.slice(0, -1)}`}
    </button>
  );
}

function OwnerLogin({ onDone }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = usePEAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await api("/auth/login", { method: "POST", body: form });
      if (!["owner", "admin", "staff"].includes(u.role)) throw new Error("This account doesn't have editing access.");
      setUser(u);
      onDone();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-72 rounded-2xl bg-[#FBF3E4] border border-stone-300 shadow-2xl p-5 space-y-3" data-testid="hub-owner-login">
      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-500">Owner sign-in</p>
      {err && <p role="alert" className="text-xs text-red-700">{err}</p>}
      <input type="email" required placeholder="Email" aria-label="Email" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="hub-owner-email" />
      <input type="password" required placeholder="Password" aria-label="Password" className={inputCls} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="hub-owner-password" />
      <button disabled={busy} data-testid="hub-owner-submit" className="w-full rounded-full bg-[#241B12] text-[#FBF3E4] py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase">
        {busy ? "Signing in…" : "Sign in to edit"}
      </button>
    </form>
  );
}

export default function HubEditBar() {
  const hub = useHub();
  const { user } = usePEAuth();
  const [showLogin, setShowLogin] = useState(false);
  if (!hub || user === undefined) return null;
  const isStaff = user && ["owner", "admin", "staff"].includes(user.role);

  return (
    <>
      <div className="fixed bottom-5 left-5 z-[90] flex flex-col items-start gap-2">
        {showLogin && !isStaff && <OwnerLogin onDone={() => setShowLogin(false)} />}
        {isStaff && hub.editMode && (
          <p className="rounded-full bg-en-green text-white px-4 py-1.5 font-mono text-[9px] tracking-[0.2em] uppercase shadow" role="status">
            Edit mode — click any ✎
          </p>
        )}
        <button
          data-testid="hub-edit-toggle"
          onClick={() => (isStaff ? hub.setEditMode(!hub.editMode) : setShowLogin((s) => !s))}
          aria-label={isStaff ? (hub.editMode ? "Leave edit mode" : "Enter edit mode") : "Owner sign in"}
          aria-pressed={isStaff && hub.editMode}
          className={`w-12 h-12 rounded-full shadow-xl flex items-center justify-center font-mono text-xs transition-all ${
            hub.editMode ? "bg-en-green text-white" : "bg-[#241B12] text-[#FBF3E4] hover:scale-105"
          }`}
        >
          {hub.editMode ? "✓" : "✳"}
        </button>
      </div>
      {hub.editor && <EditorModal />}
    </>
  );
}
